import { FC, useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from '../i18n/useTranslation';

type SortDirection = 'asc' | 'desc';

function isPrimitive(value: unknown): boolean {
  return value === null || value === undefined || typeof value !== 'object';
}

function flattenObject(obj: Record<string, unknown>, prefix = ''): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      for (const [nestedKey, nestedValue] of Object.entries(value as Record<string, unknown>)) {
        const nestedFullKey = `${fullKey}.${nestedKey}`;
        if (isPrimitive(nestedValue)) {
          result[nestedFullKey] = nestedValue;
        } else {
          result[nestedFullKey] = JSON.stringify(nestedValue);
        }
      }
    } else {
      result[fullKey] = isPrimitive(value) ? value : JSON.stringify(value);
    }
  }
  return result;
}

function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function isArrayOfObjects(data: unknown): data is Record<string, unknown>[] {
  return Array.isArray(data) && data.length > 0 && typeof data[0] === 'object' && data[0] !== null;
}

const DataTable: FC<{ rows: Record<string, unknown>[] }> = ({ rows }) => {
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDirection>('asc');

  const flatRows = useMemo(() => rows.map((r) => flattenObject(r)), [rows]);
  const columns = useMemo(() => {
    const keys = new Set<string>();
    flatRows.forEach((row) => Object.keys(row).forEach((k) => keys.add(k)));
    return Array.from(keys);
  }, [flatRows]);

  const sortedRows = useMemo(() => {
    if (!sortField) return flatRows;
    return [...flatRows].sort((a, b) => {
      const aVal = String(a[sortField] ?? '');
      const bVal = String(b[sortField] ?? '');
      const cmp = aVal.localeCompare(bVal, undefined, { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [flatRows, sortField, sortDir]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col} sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                <TableSortLabel
                  active={sortField === col}
                  direction={sortField === col ? sortDir : 'asc'}
                  onClick={() => handleSort(col)}
                >
                  {col}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.map((row, i) => (
            <TableRow
              key={i}
              sx={{
                '&:nth-of-type(odd)': { bgcolor: 'rgba(0,0,0,0.015)' },
                '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' },
              }}
            >
              {columns.map((col) => (
                <TableCell key={col} sx={{ fontSize: '0.85rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {formatCellValue(row[col])}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const KeyValueView: FC<{ data: Record<string, unknown> }> = ({ data }) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {Object.entries(data).map(([key, value]) => {
        const isComplex = !isPrimitive(value);
        return (
          <Box key={key}>
            <Box
              sx={{
                'display': 'flex',
                'alignItems': 'center',
                'py': 1.5,
                'px': 2,
                'borderBottom': '1px solid rgba(0,0,0,0.06)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.015)' },
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: 'text.secondary', minWidth: 200, flexShrink: 0 }}>
                {key}
              </Typography>
              {isComplex ? (
                <IconButton size="small" onClick={() => toggle(key)} sx={{ ml: 'auto' }}>
                  {expanded.has(key) ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              ) : (
                <Typography sx={{ fontSize: '0.85rem', color: 'text.primary' }}>
                  {formatCellValue(value)}
                </Typography>
              )}
            </Box>
            {isComplex && (
              <Collapse in={expanded.has(key)}>
                <Box sx={{ pl: 4, py: 1, bgcolor: 'rgba(0,0,0,0.02)' }}>
                  <Typography
                    component="pre"
                    sx={{
                      fontSize: '0.8rem',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      m: 0,
                    }}
                  >
                    {JSON.stringify(value, null, 2)}
                  </Typography>
                </Box>
              </Collapse>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

interface SmartDataRendererProps {
  data: unknown;
}

const SmartDataRenderer: FC<SmartDataRendererProps> = ({ data }) => {
  const t = useTranslation();

  if (!data || (typeof data === 'object' && Object.keys(data as object).length === 0)) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
          {t('visualization.noData')}
        </Typography>
      </Box>
    );
  }

  const actualData = typeof data === 'object' && !Array.isArray(data)
    ? (() => {
        const obj = data as Record<string, unknown>;
        const keys = Object.keys(obj);
        if (keys.length === 1 && Array.isArray(obj[keys[0]])) {
          return obj[keys[0]];
        }
        for (const key of ['content', 'data', 'result', 'results', 'items']) {
          if (Array.isArray(obj[key])) return obj[key];
        }
        return data;
      })()
    : data;

  if (isArrayOfObjects(actualData)) {
    return (
      <Card sx={{ overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
        <DataTable rows={actualData} />
      </Card>
    );
  }

  if (typeof actualData === 'object' && !Array.isArray(actualData) && actualData !== null) {
    return (
      <Card sx={{ overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
        <KeyValueView data={actualData as Record<string, unknown>} />
      </Card>
    );
  }

  return (
    <Card sx={{ p: 3, border: '1px solid rgba(0,0,0,0.08)', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
      <Typography sx={{ fontSize: '0.95rem' }}>{String(actualData)}</Typography>
    </Card>
  );
};

export default SmartDataRenderer;
